// The widget controller. Builds the launcher + panel inside a Shadow DOM, streams
// the conversation, and renders the typed server frames (tokens, citations, CTA
// cards, quick-reply chips). Accessible: aria-live streaming, focus management,
// keyboard, high contrast, 16px text for the 55+ reader.

import logoWhite from './assets/logo-white.png';
import type { ServerFrame, CtaCard, QuickReply, CaptureField, ChatRequest } from '@marginarc/shared';
import { STYLES } from './styles.js';
import { renderMarkdown } from './render/markdown.js';
import { streamChat, postEvent } from './stream.js';
import { resolveConfig } from './config.js';
import type { WidgetConfig } from './config.js';

const SID_KEY = 'ma_sid';

export function mountWidget(opts: Partial<WidgetConfig> & { apiBase: string }): void {
  if (document.getElementById('marginarc-assistant-root')) return;
  injectFonts();
  const host = document.createElement('div');
  host.id = 'marginarc-assistant-root';
  document.body.appendChild(host);
  const root = host.attachShadow({ mode: 'open' });
  new Widget(root, resolveConfig(opts));
}

function injectFonts(): void {
  if (document.getElementById('ma-fonts')) return;
  const l = document.createElement('link');
  l.id = 'ma-fonts';
  l.rel = 'stylesheet';
  l.href =
    'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@600;700&family=Inter:wght@400;500;600&display=swap';
  document.head.appendChild(l);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
  ...kids: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (attrs) for (const [k, v] of Object.entries(attrs)) (k === 'class' ? (n.className = v) : n.setAttribute(k, v));
  for (const kid of kids) n.append(kid);
  return n;
}

interface BotMsg {
  row: HTMLElement;
  bubble: HTMLElement;
  cites: HTMLElement | null;
  text: string;
  messageId: string;
}

class Widget {
  private cfg: WidgetConfig;
  private root: ShadowRoot;
  private sessionId: string;
  private launcher!: HTMLButtonElement;
  private panel!: HTMLElement;
  private msgs!: HTMLElement;
  private input!: HTMLTextAreaElement;
  private send!: HTMLButtonElement;
  private open = false;
  private busy = false;
  private greeted = false;

  constructor(root: ShadowRoot, cfg: WidgetConfig) {
    this.root = root;
    this.cfg = cfg;
    this.sessionId = this.loadSid();
    this.build();
    postEvent(cfg.apiBase, 'widget_shown', this.sessionId);
  }

  private loadSid(): string {
    try {
      let id = localStorage.getItem(SID_KEY);
      if (!id) {
        id = (crypto.randomUUID?.() ?? String(Date.now()) + Math.random().toString(16).slice(2));
        localStorage.setItem(SID_KEY, id);
      }
      return id;
    } catch {
      return String(Date.now());
    }
  }

  private build(): void {
    const style = el('style');
    style.textContent = STYLES;
    this.root.append(style);

    // Launcher
    this.launcher = el('button', { class: 'launcher', 'aria-label': this.cfg.launcherLabel });
    this.launcher.append(logoImg(20), el('span', {}, this.cfg.launcherLabel));
    this.launcher.addEventListener('click', () => this.openPanel());
    this.root.append(this.launcher);

    // Panel
    this.panel = el('section', { class: 'panel', role: 'dialog', 'aria-label': this.cfg.title, 'aria-modal': 'false', hidden: '' });

    const head = el('div', { class: 'head' });
    const close = el('button', { class: 'close', 'aria-label': 'Close assistant' }, '×');
    close.addEventListener('click', () => this.closePanel());
    head.append(
      logoImg(20),
      el('span', { class: 'spacer' }),
      el('span', { class: 'status' }, el('span', { class: 'dot' }), 'AI Assistant'),
      close,
    );

    this.msgs = el('div', { class: 'msgs', role: 'log', 'aria-live': 'polite', 'aria-label': 'Conversation' });

    const foot = el('div', { class: 'foot' });
    const form = el('form', { class: 'form' });
    this.input = el('textarea', { class: 'input', rows: '1', placeholder: this.cfg.placeholder, 'aria-label': 'Message' }) as HTMLTextAreaElement;
    this.input.addEventListener('input', () => this.autosize());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.submit();
      }
    });
    this.send = el('button', { class: 'send', type: 'submit', 'aria-label': 'Send message' }, sendIcon());
    form.append(this.input, this.send);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
    foot.append(form, el('p', { class: 'consent' }, this.cfg.consent));

    this.panel.append(head, this.msgs, foot);
    this.root.append(this.panel);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.open) this.closePanel();
    });

    // Focus containment: while the dialog is open, keep Tab/Shift+Tab inside the
    // panel instead of dropping focus to the page behind it (a11y).
    this.panel.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !this.open) return;
      const f = Array.from(
        this.panel.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),textarea,input,[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((n) => n.offsetParent !== null);
      const first = f[0];
      const last = f[f.length - 1];
      if (!first || !last) return;
      const active = this.root.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  private autosize(): void {
    this.input.style.height = 'auto';
    this.input.style.height = `${Math.min(this.input.scrollHeight, 120)}px`;
  }

  private openPanel(): void {
    this.open = true;
    this.panel.hidden = false;
    this.launcher.classList.add('hidden');
    postEvent(this.cfg.apiBase, 'widget_opened', this.sessionId);
    if (!this.greeted) {
      this.greeted = true;
      this.addBotStatic(this.cfg.greeting);
      this.addOpenerChips();
    }
    setTimeout(() => this.input.focus(), 50);
  }

  private closePanel(): void {
    this.open = false;
    this.panel.hidden = true;
    this.launcher.classList.remove('hidden');
    this.launcher.focus();
  }

  private submit(): void {
    const text = this.input.value.trim();
    if (!text || this.busy) return;
    this.input.value = '';
    this.autosize();
    void this.sendMessage(text);
  }

  private setBusy(b: boolean): void {
    this.busy = b;
    this.send.disabled = b;
  }

  private async sendMessage(text: string, chip?: NonNullable<ChatRequest['chip']>): Promise<void> {
    this.addUser(text);
    this.setBusy(true);
    const ctx = this.startBot();
    const req: ChatRequest = { sessionId: this.sessionId, message: text, page: { url: location.pathname }, ...(chip ? { chip } : {}) };
    try {
      await streamChat(this.cfg.apiBase, req, (f) => this.onFrame(f, ctx));
    } catch {
      if (!ctx.text) {
        ctx.bubble.innerHTML = renderMarkdown(
          "I couldn't reach the assistant just now. Please try again, or email matt@marginarc.com.",
        );
      }
    } finally {
      this.removeTyping(ctx);
      this.addFeedback(ctx);
      this.setBusy(false);
      this.input.focus();
    }
  }

  private onFrame(f: ServerFrame, ctx: BotMsg): void {
    switch (f.type) {
      case 'start':
        ctx.messageId = f.messageId;
        break;
      case 'token':
        this.removeTyping(ctx);
        ctx.text += f.text;
        ctx.bubble.innerHTML = renderMarkdown(ctx.text);
        this.scroll();
        break;
      case 'replace':
        ctx.text = '';
        ctx.bubble.innerHTML = '';
        break;
      case 'citation':
        this.addCitation(ctx, f.citation.label, f.citation.target);
        break;
      case 'cta':
        this.addCards(ctx, f.cards);
        break;
      case 'chips':
        this.addChips(f.chips);
        break;
      case 'capture':
        this.addCapture(f.prompt, f.fields);
        break;
      case 'error':
        this.removeTyping(ctx);
        if (!ctx.text) ctx.bubble.innerHTML = renderMarkdown(f.message);
        break;
      case 'fallback':
      case 'done':
        break;
    }
  }

  // ---- rendering helpers ----
  private addUser(text: string): void {
    const row = el('div', { class: 'row user' });
    row.append(el('div', { class: 'bubble' }, text));
    this.msgs.append(row);
    this.scroll();
  }

  private addBotStatic(text: string): void {
    const row = el('div', { class: 'row bot' });
    const bubble = el('div', { class: 'bubble' });
    bubble.innerHTML = renderMarkdown(text);
    row.append(bubble);
    this.msgs.append(row);
    this.scroll();
  }

  private startBot(): BotMsg {
    const row = el('div', { class: 'row bot' });
    const bubble = el('div', { class: 'bubble' });
    const typing = el('div', { class: 'typing', 'aria-label': 'Assistant is typing' }, el('span'), el('span'), el('span'));
    typing.setAttribute('data-typing', '1');
    bubble.append(typing);
    row.append(bubble);
    this.msgs.append(row);
    this.scroll();
    return { row, bubble, cites: null, text: '', messageId: '' };
  }

  private removeTyping(ctx: BotMsg): void {
    const t = ctx.bubble.querySelector('[data-typing]');
    if (t) t.remove();
  }

  private addCitation(ctx: BotMsg, label: string, target: string): void {
    if (!ctx.cites) {
      ctx.cites = el('div', { class: 'cites' });
      ctx.row.append(ctx.cites);
    }
    const a = el('a', { class: 'cite', href: target, target: '_blank', rel: 'noopener' }, label);
    ctx.cites.append(a);
    this.scroll();
  }

  private addCards(ctx: BotMsg, cards: CtaCard[]): void {
    const wrap = el('div', { class: 'cards' });
    for (const c of cards) {
      const a = el('a', { class: `card ${c.style}`, href: c.target }, el('span', {}, c.label), el('span', { class: 'arrow' }, '→'));
      a.addEventListener('click', () => postEvent(this.cfg.apiBase, 'cta_clicked', this.sessionId, { cta: c.id, target: c.target }));
      wrap.append(a);
    }
    ctx.row.append(wrap);
    this.scroll();
  }

  // Static starter chips under the greeting. The "look at my website" one is handled
  // client-side: it asks for the URL (the server-side website read only fires on a real
  // address), so tapping it never sends a bare "look at my website" to the model.
  private addOpenerChips(): void {
    const items = this.cfg.openerChips;
    if (!items?.length) return;
    const wrap = el('div', { class: 'chips' });
    for (const label of items) {
      const b = el('button', { class: 'chip', type: 'button' }, label);
      const isTeaser = /website/i.test(label);
      b.addEventListener('click', () => {
        postEvent(this.cfg.apiBase, 'chip_tapped', this.sessionId, { label });
        wrap.remove();
        if (isTeaser) {
          this.addUser(label); // show the tapped question so the reply has context
          this.addBotStatic(this.cfg.teaserReply);
          setTimeout(() => this.input.focus(), 50);
        } else {
          void this.sendMessage(label);
        }
      });
      wrap.append(b);
    }
    this.msgs.append(wrap);
    this.scroll();
  }

  private addChips(chips: QuickReply[]): void {
    const wrap = el('div', { class: 'chips' });
    for (const c of chips) {
      const b = el('button', { class: 'chip', type: 'button' }, c.label);
      b.addEventListener('click', () => {
        postEvent(this.cfg.apiBase, 'chip_tapped', this.sessionId, { label: c.label });
        wrap.remove();
        void this.sendMessage(c.label, c.field ? { field: c.field, value: c.value } : undefined);
      });
      wrap.append(b);
    }
    this.msgs.append(wrap);
    this.scroll();
  }

  // Inline lead form (the dormant `capture` frame). One field (email) + a honeypot.
  private addCapture(prompt: string, _fields: CaptureField[]): void {
    if (this.root.querySelector('.capture')) return; // one form per session
    const wrap = el('div', { class: 'capture' });
    wrap.append(el('p', { class: 'capture-prompt' }, prompt));
    const form = el('form', { class: 'capture-form' });
    const email = el('input', {
      class: 'capture-input', type: 'email', placeholder: 'you@company.com',
      'aria-label': 'Your email', autocomplete: 'email', inputmode: 'email',
    }) as HTMLInputElement;
    const hp = el('input', {
      class: 'capture-hp', type: 'text', tabindex: '-1', autocomplete: 'off', 'aria-hidden': 'true',
    }) as HTMLInputElement;
    const btn = el('button', { class: 'capture-send', type: 'submit' }, 'Send it') as HTMLButtonElement;
    form.append(email, hp, btn);
    const status = el('p', { class: 'capture-status', role: 'status' });
    wrap.append(form, status);

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const val = email.value.trim();
      if (!val) { status.textContent = 'Please enter your email.'; return; }
      btn.disabled = true;
      status.textContent = '';
      void fetch(`${this.cfg.apiBase}/capture`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ sessionId: this.sessionId, email: val, hp: hp.value }),
      })
        .then((r) => (r.ok ? r.json() : { ok: false }))
        .then((d: { ok?: boolean }) => {
          if (d.ok) { form.remove(); status.textContent = "Got it. Matt will follow up."; }
          else { btn.disabled = false; status.textContent = "That email didn't look right. Try again."; }
        })
        .catch(() => { btn.disabled = false; status.textContent = "Couldn't send just now. Try again."; });
    });

    this.msgs.append(wrap);
    this.scroll();
  }

  private addFeedback(ctx: BotMsg): void {
    if (!ctx.text || ctx.row.querySelector('.feedback')) return;
    const fb = el('div', { class: 'feedback' });
    const up = el('button', { 'aria-label': 'Helpful', 'aria-pressed': 'false', title: 'Helpful' }, '\u{1F44D}');
    const down = el('button', { 'aria-label': 'Not helpful', 'aria-pressed': 'false', title: 'Not helpful' }, '\u{1F44E}');
    const vote = (btn: HTMLButtonElement, value: 'up' | 'down'): void => {
      up.setAttribute('aria-pressed', String(btn === up));
      down.setAttribute('aria-pressed', String(btn === down));
      this.postFeedback(ctx.messageId, value);
    };
    up.addEventListener('click', () => vote(up, 'up'));
    down.addEventListener('click', () => vote(down, 'down'));
    fb.append(up, down);
    ctx.row.append(fb);
  }

  private postFeedback(messageId: string, value: 'up' | 'down'): void {
    try {
      void fetch(`${this.cfg.apiBase}/feedback`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ sessionId: this.sessionId, messageId, value }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* no-op */
    }
  }

  private scroll(): void {
    this.msgs.scrollTop = this.msgs.scrollHeight;
  }
}

function logoImg(h: number): HTMLImageElement {
  return el('img', { src: logoWhite, alt: 'MarginArc', height: String(h) }) as HTMLImageElement;
}

function sendIcon(): HTMLElement {
  const span = el('span', { 'aria-hidden': 'true' });
  span.innerHTML =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>';
  return span;
}
